import type { FootprintVariant } from '@onefootprint/footprint-js-legacy';
import { cx } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import use100vh from './hooks/use-100vh';

type FullHeightContainerProps = HTMLAttributes<HTMLDivElement> & {
  variant?: FootprintVariant;
  hasBorderRadius?: boolean;
  isSandbox?: boolean;
  fitContent?: boolean;
  fullBleed?: boolean;
};

const INLINE_FIT_CONTENT_MIN_HEIGHT = 550;

// In iPhones, with the recent changes to move the URL bar and buttons
// to the bottom of the browser page (as overlays), 100vh no longer
// refers to the height of the screen.
// See: https://bugs.webkit.org/show_bug.cgi?id=141832#c5
// Apple engineers replied saying 'it is a feature, not a bug'.
// This component resizes itself to the actual height of the screen.
const FullHeightContainer = forwardRef<HTMLDivElement, FullHeightContainerProps>(
  (
    { variant = 'modal', id, hasBorderRadius, children, isSandbox = false, fitContent = false, fullBleed = false },
    ref,
  ) => {
    const viewportHeight = use100vh();
    const height = viewportHeight ? `${viewportHeight}px` : '100vh';
    const isInlineFitContent = variant === 'inline' && fitContent;

    const getVariantClasses = () => {
      if (variant === 'inline') {
        return isInlineFitContent ? cx('h-auto w-screen') : cx('h-full w-screen');
      }
      if (variant === 'drawer') {
        return cx('w-full', 'md:h-screen md:max-h-none md:fixed md:right-0');
      }
      // modal variant (default)
      return cx('w-screen', 'md:h-auto');
    };

    // Only dynamic height values need inline styles
    const dynamicStyles: React.CSSProperties = {};
    if (variant === 'modal') {
      // Set as CSS custom property so we can override with 'auto' on desktop via CSS
      (dynamicStyles as Record<string, string>)['--mobile-height'] = height;
    } else if (variant === 'drawer') {
      dynamicStyles.height = height;
    } else if (isInlineFitContent) {
      dynamicStyles.minHeight = `${INLINE_FIT_CONTENT_MIN_HEIGHT}px`;
    }

    return (
      <div
        id={id}
        ref={ref}
        className={cx(
          'flex flex-col overflow-y-auto relative',
          getVariantClasses(),
          'data-[scroll-lock="true"]:overflow-y-hidden',
        )}
        data-variant={variant}
        data-sandbox={isSandbox || undefined}
        data-has-border-radius={hasBorderRadius || undefined}
        data-full-bleed={fullBleed || undefined}
        style={dynamicStyles}
      >
        {children}
      </div>
    );
  },
);

export default FullHeightContainer;
